import { CellProps, Column } from "react-table"
import { Tab, Tabs, styledTabClasses } from "components/Tabs"
import { css, cx } from "@emotion/css"
import { deleteAgencyClientTeam, fetchAgencyClientTeams } from "api/team"
import { fetchAgencyClientTeamLeads, fetchAgencyClientUsers } from "api/clients"
import { queryCache, useMutation, useQuery } from "react-query"

import { AgencyClientTeamLeads } from "./AgencyClientTeamLeads"
import { AssignTeams } from "./AssignTeams"
import { Box } from "@material-ui/core"
import Button from "ui/buttons/Button"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import { EmptyState } from "components/EmptyState"
import { Link } from "components/Link"
import MoreActions from "components/MoreActions"
import React from "react"
import { Table } from "components/tables"
import { Team } from "models/common"
import { ReactComponent as TeamsIcon } from "assets/icon-team.svg"
import { WithAuthority } from "components/WithAuthority"
import { limitAndOffset } from "api/utils"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useParams } from "react-router-dom"
import { useToggle } from "hooks/useToggle"
import { useAgencyId } from "hooks/useAgencyId"

type GetColumns = (onDelete: (id: string) => void) => Array<Column<Team>>

const getColumns: GetColumns = (onDelete) => {
  return [
    {
      header: "agency.client.team.name",
      id: "name",
      Cell: function (props: CellProps<Team>) {
        const { name } = props.row.original
        return (
          <Link color="primary" to={`/agency-teams`}>
            {name}
          </Link>
        )
      },
    },
    {
      header: "agency.client.team.users",
      id: "users",
      Cell: (props: CellProps<Team>) => {
        const { row } = props
        const users = row.original.userCount
        return users || "-"
      },
    },
    {
      align: "right",
      header: "clients.list.actions",
      id: "actions",
      Cell: function (props: CellProps<Team>) {
        const { t } = useI18n()
        const { id } = props.row.original
        const { hasAuthority } = useAuthenticated()

        return (
          <MoreActions
            options={[
              // DELETE
              {
                hidden: !hasAuthority(["AUTH_UNASSIGN_AGENCY_CLIENT_TEAM"]),
                label: t("client-groups.list.delete.action"),
                icon: <DeleteIcon />,
                onClick: () => {
                  onDelete(id)
                },
              },
            ]}
          />
        )
      },
    },
  ]
}

export function AgencyClientTeams() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const { open, onClose, onOpen } = useToggle(false)
  const { hasAuthority } = useAuthenticated()
  const ls = useLocationSearch()
  const params = ls.map(limitAndOffset).toString()

  const [activeTab, setActiveTab] = React.useState(() => "planning")
  const { agencyId } = useAgencyId()

  const { data: teamLeads } = useQuery(
    ["agency-client-team-leads", id, agencyId],
    fetchAgencyClientTeamLeads,
    {
      enabled: !!agencyId,
    },
  )

  const { data: members } = useQuery(
    ["agency-client-users", id, agencyId],
    fetchAgencyClientUsers,
    {
      enabled: !!agencyId,
    },
  )

  const { data: teams, status } = useQuery(
    ["agency-client-teams", id, agencyId, params],
    fetchAgencyClientTeams,
    {
      enabled: !!agencyId,
    },
  )

  const [deleteTeam] = useMutation(deleteAgencyClientTeam, {
    onSuccess: () => queryCache.invalidateQueries("agency-client-teams"),
  })

  const onDelete = React.useCallback(
    (teamId: string) => {
      !!agencyId && deleteTeam({ clientId: id, teamId, agencyId })
    },
    [deleteTeam, id, agencyId],
  )

  const isLoading = status === "idle" || status === "loading"
  const columns = getColumns(onDelete)
  const existingAgencyIds = teams?.map((x) => x.id) ?? []

  const actionButton = (
    <div>
      {hasAuthority(["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"]) && (
        <Button onClick={onOpen} variant="contained" color="primary">
          {t("agency.client.teams.assign")}
        </Button>
      )}
    </div>
  )

  return (
    <Box className={styles.dataWrapper}>
      <span className="BodyBody-1BlackBold">
        {t("agency.client.team.leads")}
      </span>
      <WithAuthority allowed={["AUTH_VIEW_AGENCY_CLIENT_TEAM"]}>
        <div className={styles.box}>
          <Tabs
            value={activeTab}
            onTabChange={setActiveTab}
            TabIndicatorProps={{
              style: {
                display: "none",
              },
            }}
            classes={{ flexContainer: styles.tabs }}
          >
            <Tab
              label={t("agency.client.team.leader.planning")}
              value="planning"
              classes={styledTabClasses}
              className={styles.tab}
            />
            <Tab
              label={t("agency.client.team.leader.buying")}
              value="buying"
              classes={styledTabClasses}
              className={styles.tab}
            />
          </Tabs>
          <div className={styles.wrapper}>
            {activeTab === "planning" ? (
              <AgencyClientTeamLeads
                agencyId={agencyId}
                teamLeads={teamLeads}
                members={members}
                role="PLANNER"
              />
            ) : null}
            {activeTab === "buying" ? (
              <AgencyClientTeamLeads
                agencyId={agencyId}
                teamLeads={teamLeads}
                members={members}
                role="BUYER"
              />
            ) : null}
          </div>
        </div>
      </WithAuthority>
      <Table<Team>
        id="admin-agency-client-teams"
        loading={isLoading}
        data={teams || []}
        columns={columns}
        filters={[]}
        searchBy="name"
        total={teams?.length || 0}
        hasColumnsReordering={false}
        noResults={
          <EmptyState
            title={t("agency.client.teams.noresults.title")}
            subtitle={
              hasAuthority(["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"])
                ? t("agency.client.teams.noresults.subtitle")
                : undefined
            }
            icon={<TeamsIcon />}
          >
            {hasAuthority(["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"]) ? (
              <span
                onClick={onOpen}
                className={cx(
                  "BodyBody-1Primary-mainRegular",
                  styles.createNew,
                )}
              >
                {t("team.add_button")}
              </span>
            ) : null}
          </EmptyState>
        }
        actionButton={actionButton}
      />
      {!!agencyId ? (
        <AssignTeams
          open={open}
          onClose={onClose}
          agencyId={agencyId}
          existingAgencyIds={existingAgencyIds}
        />
      ) : null}
    </Box>
  )
}

const styles = {
  box: css`
    display: flex;
    flex-direction: column;
    width: 540px;
    margin: var(--spacing-xxl-2) 524px var(--spacing-xxl-6) 0px;
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
  dataWrapper: css`
    padding: var(--spacing-xxl-2) 0px 0px;
  `,
  tabs: css`
    border-bottom: 1px solid var(--light-grey);
    height: 32px;
    min-width: fit-content;
  `,
  tab: css`
    min-height: var(--spacing-xxl-2);
    &.MuiButtonBase-root {
      background-color: var(--light-grey-2);
      padding: var(--spacing-xs) var(--spacing-m);
      min-width: 0;
      border: solid 1px var(--light-grey);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      border-bottom: solid 1px var(--light-grey);
      span {
        font-size: 13px;
        color: var(--grey);
        text-transform: none;
      }
    }
    &.MuiButtonBase-root.Mui-selected {
      background-color: var(--white);
      border-bottom: solid 1px var(--white);
      span {
        color: var(--black);
        font-weight: normal;
      }
    }
  `,
  wrapper: css`
    display: flex;
    flex-direction: column;
    padding: var(--spacing-xxl-2) var(--spacing-xl);
    border-radius: 3px;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.16);
    border-style: solid;
    border-color: var(--light-grey);
    border-width: 0 1px 1px 1px;
    margin-top: -16px;
    min-height: 248px;
  `,
}
